import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faCode } from '@fortawesome/free-solid-svg-icons';
import config from '../../configs/config';
import { useEscapeKey } from '../hooks/useEscapeKey';
import './styles/CustomCodeModal.css';

// Survey-wide custom CSS/JS (like Confirmit/Decipher's "Custom CSS/JS"
// project settings) - applies to every page of the live survey, on top of
// any per-question customCSS/customJs. Stored in
// project_file.config_json.customCSS / config_json.customJs (PostgreSQL),
// read by PollStudioNST's fetch_survey_row() and injected into the
// respondent page by survey.page.ts.
const CustomCodeModal = ({ isOpen, onClose, projectId, onSave }) => {
  const [css, setCss] = useState('');
  const [js, setJs] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !projectId) return;

    const loadCustomCode = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${config.API_BASE_URL}/get-custom-code-config/${projectId}`, {
          credentials: 'include',
        });
        const result = await res.json();
        if (result.success) {
          setCss(result.customCSS || '');
          setJs(result.customJs || '');
        } else {
          setCss('');
          setJs('');
        }
      } catch (err) {
        console.error('Failed to load custom code:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCustomCode();
  }, [isOpen, projectId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/save-custom-code-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ projectId, customCSS: css, customJs: js }),
      });
      const result = await res.json();
      if (result.success) {
        onSave?.({ css, js });
        onClose();
      } else {
        alert('Failed to save custom code: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error saving custom code:', err);
      alert('Error saving custom code');
    } finally {
      setSaving(false);
    }
  };

  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="customcode-modal-overlay" onClick={onClose}>
      <div className="customcode-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="customcode-modal-header">
          <h3>
            <FontAwesomeIcon icon={faCode} style={{ marginRight: '8px' }} />
            Custom CSS / JS
          </h3>
          <button className="customcode-close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="customcode-modal-body">
          {loading ? (
            <p className="customcode-loading">Loading custom code...</p>
          ) : (
            <>
              <p className="customcode-help">
                Applies to every page of this survey, on top of any per-question custom CSS/JS.
                This code runs on the live respondent page - only paste code you trust.
              </p>

              <div className="customcode-field-block">
                <label htmlFor="customcode-css">Custom CSS</label>
                <textarea
                  id="customcode-css"
                  className="customcode-textarea"
                  rows={8}
                  value={css}
                  onChange={(e) => setCss(e.target.value)}
                  placeholder=".question-title { color: #333; }"
                  spellCheck={false}
                />
              </div>

              <div className="customcode-field-block">
                <label htmlFor="customcode-js">Custom JS</label>
                <textarea
                  id="customcode-js"
                  className="customcode-textarea"
                  rows={8}
                  value={js}
                  onChange={(e) => setJs(e.target.value)}
                  placeholder="console.log('survey loaded');"
                  spellCheck={false}
                />
              </div>
            </>
          )}
        </div>

        <div className="customcode-modal-footer">
          <button className="customcode-cancel-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="customcode-save-btn" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving...' : (
              <>
                <FontAwesomeIcon icon={faSave} /> Save
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomCodeModal;
